// src/components/AgentSelectionPopup.tsx

import React, { useState } from "react";
import {
    Box,
    Typography,
    TextField,
    InputAdornment,
    Paper,
    ListItemButton,
    Tooltip,
    Avatar,
} from "@mui/material";
import { 
    Search as SearchIcon, 
    CheckCircle as CheckCircleIcon 
} from "@mui/icons-material";

export interface Agent {
    id: string;
    name: string;
    description: string;
    provider: 'aws' | 'gcp' | 'azure' | 'generic';
}

const allAgents: Agent[] = [
    { 
        id: 'agent-1', 
        name: 'Data Monitoring Agent', 
        description: 'Agent responsible for monitoring data streams, system metrics, and alerts.', 
        provider: 'gcp' 
    },
    { 
        id: 'agent-2', 
        name: 'Ops Assistant', 
        description: 'Assists with day-to-day operations, deployments, and infrastructure management.', 
        provider: 'gcp' 
    },
    { 
        id: 'agent-3', 
        name: 'Analysis Agent', 
        description: 'Analyzes incoming data, generates reports, and provides operational insights.', 
        provider: 'gcp' 
    },
    { 
        id: 'agent-4', 
        name: 'Support Agent', 
        description: 'Your go-to expert for comprehensive technical support, debugging, and deep-dive architectural troubleshooting.', 
        provider: 'gcp' 
    }
];

interface AgentSelectionPopupProps {
    onClose: () => void;
    onSelectAgent: (agent: Agent) => void;
    selectedAgentId: string;
}

const AgentSelectionPopup: React.FC<AgentSelectionPopupProps> = ({ onClose, onSelectAgent, selectedAgentId }) => {
    const [searchQuery, setSearchQuery] = useState("");

    const filteredAgents = allAgents.filter(agent => 
        agent.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        agent.description.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
        <Box sx={{ p: 2.5, width: { xs: '300px', sm: '380px' }, maxHeight: '60vh', overflowY: 'auto' }}>
            <Typography
                variant="h6"
                sx={{
                    fontFamily: "Gilroy-SemiBold",
                    fontSize: '20px',
                    color: "#1F2937",
                    mb: 2,
                }}
            >
                AI Agents
            </Typography>

            <TextField
                variant="outlined"
                placeholder="Search"
                fullWidth
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{
                    mb: 2,
                    '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                        fontFamily: 'Gilroy-Regular',
                        bgcolor: '#F9FAFB',
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E5E7EB' },
                    },
                    '& .MuiOutlinedInput-input': {
                            paddingTop: '12px', 
                            paddingBottom: '12px',
                        },
                }}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <SearchIcon sx={{ color: '#101010' }} />
                        </InputAdornment>
                    ),
                }}
            />

            <Typography
                variant="body2"
                sx={{ fontFamily: 'Gilroy-Medium', color: '#4B5563', mb: 2 }}
            >
                Choose any AI agent against which you want to prompt and get results
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {filteredAgents.map((agent) => {
                    const isSelected = agent.id === selectedAgentId;
                    return (
                        <Paper
                            key={agent.id}
                            elevation={0}
                            sx={{
                                border: '2px solid',
                                borderColor: isSelected ? '#19AF66' : '#E5E7EB',
                                borderRadius: '8px',
                                position: 'relative',
                                '&:hover': {
                                    borderColor: isSelected ? '#19AF66' : '#D1D5DB',
                                }
                            }}
                        >
                            <ListItemButton
                                onClick={() => onSelectAgent(agent)}
                                sx={{
                                    borderRadius: '8px',
                                    p: '16px',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                }}
                            >
                                <Box sx={{ width: 'calc(100% - 40px)' }}> 
                                    <Typography sx={{ fontFamily: 'Gilroy-Bold', fontSize: '16px', color: '#1F2937' }}>
                                        {agent.name}
                                    </Typography>
                                    
                                    <Tooltip title={agent.description} arrow>
                                        <Typography sx={{ 
                                            fontFamily: 'Gilroy-Regular', 
                                            fontSize: '14px', 
                                            color: '#6B7280', 
                                            mt: 0.5,
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                        }}>
                                            {agent.description}
                                        </Typography>
                                    </Tooltip>
                                </Box>
                                
                                <Avatar
                                    sx={{
                                        width: 32,
                                        height: 32,
                                        ml: 1,
                                        flexShrink: 0,
                                        bgcolor: '#FFFFFF',
                                        border: '1px solid #E5E7EB'
                                    }}
                                >
                                    <Box
                                        component="img"
                                        src="/assets/agent-image.svg"
                                        alt="Agent"
                                        sx={{ width: 'auto', height: 'auto', padding: '4.5px 3.5px' }}
                                    />
                                </Avatar>
                            </ListItemButton>
                            
                            {isSelected && (
                                <CheckCircleIcon
                                    sx={{
                                        color: '#19AF66',
                                        position: 'absolute',
                                        top: -10,
                                        right: -10,
                                        fontSize: '20px',
                                        zIndex:2
                                    }}
                                />
                            )}
                        </Paper>
                    );
                })}
            </Box>
        </Box>
    );
};

export default AgentSelectionPopup;